"use client";

import React from "react";
import { MapPin, Navigation, Phone, Mail, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const SHARED_DATA = {
  address: "315 5th Ave, New York City, NY 10016",
  phone: "(212) 804-7665",
  email: "info@samuelsharmatmd.com",
  googleMapsUrl: "https://maps.google.com/?q=123+Main+St,+New+York+City,+NY+10001",
  states: ["New York", "New Jersey", "Connecticut", "Florida", "Illinois", "Tennessee", "Vermont", "Washington"]
};
export default function AccessingCareAnywhere() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="accessing-care-anywhere" ref={ref} className="relative w-full overflow-hidden transition-opacity duration-1000">
      {}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/3 w-px h-full from-transparent via-border to-transparent" />
        <div className="absolute top-1/2 left-0 w-full h-px from-transparent via-border to-transparent" />
      </div>

      <div className="container mx-auto px-4 py-20 lg:py-32 relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
          
          {}
          <div className="w-full lg:w-2/5 order-2 lg:order-1">
            <div className={`transition-all duration-700 delay-200 transform ${isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
              <Card className="border-border/60 shadow-2xl bg-card/80 backdrop-blur-sm overflow-hidden">
                <div className="relative h-48 w-full overflow-hidden bg-muted">
                  <img src="https://vast-cheetah.10web.cloud/wp-content/uploads/2026/04/315-Fifth-Photo-377x240-1.jpeg" alt="Sharmat Psychiatric Group NYC Office Facade" className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" />
                  <div className="absolute inset-0 from-black/60 to-transparent flex items-end p-6">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white text-xs font-medium">
                      <MapPin className="w-3 h-3" />
                      NYC Headquarters
                    </span>
                  </div>
                </div>

                <CardHeader className="pb-2">
                  <CardTitle className="text-2xl font-serif text-foreground">
                    In-Person & Remote
                  </CardTitle>
                  <CardDescription className="text-base mt-2">
                    Our main office provides a calm place for in-person evaluations, while our secure telehealth platform brings expert care to your home.
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-4 pt-4">
                  <div className="flex items-start gap-3 text-sm text-muted-foreground">
                    <Navigation className="w-4 h-4 mt-1 text-primary shrink-0" />
                    <div>
                      <p className="font-medium text-foreground">Main Office</p>
                      <p>{SHARED_DATA.address}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 text-sm text-muted-foreground">
                    <Phone className="w-4 h-4 text-primary shrink-0" />
                    <Link to={`tel:${SHARED_DATA.phone.replace(/\D/g, '')}`} className="hover:text-primary transition-colors">
                      {SHARED_DATA.phone}
                    </Link>
                  </div>

                  <div className="flex items-center gap-3 text-sm text-muted-foreground">
                    <Mail className="w-4 h-4 text-primary shrink-0" />
                    <Link to={`mailto:${SHARED_DATA.email}`} className="hover:text-primary transition-colors">
                      {SHARED_DATA.email}
                    </Link>
                  </div>
                </CardContent>

                <CardFooter className="pt-2 gap-3 flex-col sm:flex-row">
                  <Button asChild className="w-full sm:w-auto" variant="default">
                    <Link to={SHARED_DATA.googleMapsUrl} newTab>
                      Get Directions
                    </Link>
                  </Button>
                  <Button asChild variant="outline" className="w-full sm:w-auto">
                    <Link to="#">
                      Contact Us <ArrowRight className="w-4 h-4 ml-2" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </div>
          </div>

          {}
          <div className="w-full lg:w-3/5 order-1 lg:order-2">
            <div className={`transition-all duration-1000 delay-300 transform ${isVisible ? "translate-x-0 opacity-100" : "translate-x-10 opacity-0"}`}>
              <div className="mb-8 text-center lg:text-left">
                {}
                <h2 className="text-3xl md:text-4xl font-serif text-foreground mb-4">
                  Accessing Care <span className="text-accent">Near & Far</span>
                </h2>
                <p className="text-muted-foreground text-lg max-w-xl mx-auto lg:mx-0">
                  Bridging the physical and digital. We provide comprehensive psychiatric care remotely across multiple states and in-person at our New York City office.
                </p>
              </div>

              {}
              <div className="w-full min-h-[300px] bg-muted/30 rounded-xl border-2 flex items-center justify-center overflow-hidden">
                <img src="https://vast-cheetah.10web.cloud/wp-content/uploads/2026/04/US-map-scaled-e1777063629571.png" alt="US map" className="w-full h-auto object-contain" />
              </div>

              <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-4">
                {SHARED_DATA.states.map((state, i) => <div key={state} data-index={i} className="flex items-center gap-2 px-3 py-2 rounded-md border border-border backdrop-blur-sm !bg-[hsl(215,20%,65%)]">
                    <div className="w-1.5 h-1.5 rounded-full bg-accent shadow-[0_0_5px_var(--color-accent)]" />
                    <span className="text-sm font-medium text-foreground">{state}</span>
                  </div>)}
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>;
}